// Section 15: deaths at health 0, named by the dominant cause of this season's health loss, and
// the elders' age-based death roll (1% per season at 60 rising to 12% at 85).
import { peopleSorted } from '../../core/queries';
import type { DeathCause, HealthLossCause, Person, PhaseContext } from '../../core/types';
import { killPerson } from '../shared/death';
import { num } from '../shared/value';

const CAUSE: Record<HealthLossCause, DeathCause> = {
  hunger: 'starvation',
  thirst: 'dehydration',
  exposure: 'exposure',
  cold: 'exposure',
  age: 'old-age',
  illness: 'illness',
};

function causeOf(ctx: PhaseContext, p: Person): DeathCause {
  const losses = ctx.report.current.healthLoss[p.id] ?? {};
  let best: HealthLossCause | null = null;
  for (const [cause, amount] of Object.entries(losses) as [HealthLossCause, number][]) {
    if (best === null || amount > (losses[best] ?? 0)) best = cause;
  }
  return best === null ? 'old-age' : CAUSE[best];
}

export function resolveDeaths(ctx: PhaseContext): void {
  // Scan the committed state (health was settled by the bodies phase); draft only to kill.
  for (const p of peopleSorted(ctx.state)) {
    if (p.health <= 0) {
      const cause = causeOf(ctx, p);
      const illnessId = p.status.sick?.illnessId;
      killPerson(
        ctx,
        p.id,
        cause,
        cause === 'illness' && illnessId !== undefined ? { illnessId } : {},
      );
      continue;
    }
    const chance = num(ctx, { kind: 'elderDeath', personId: p.id });
    if (chance > 0 && ctx.rng.happened(ctx.rng.chance(chance))) killPerson(ctx, p.id, 'old-age');
  }
}
